import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Button } from '@/components/atoms/Button';
import { TagBadge, type TagCategory } from '@/components/atoms/TagBadge';
import { toast } from '@/store/toastStore';
import { cn } from '@/lib/cn';

/**
 * 어드민 종목 태그 관리 — Phase 8
 *
 * 4개 탭:
 *   1) 태그 마스터 — 활성/비활성 토글, description/color 수정 (super)
 *   2) 종목 태그   — 종목 검색 후 수동 태그 추가/제거 (content 이상)
 *   3) AI 트리거   — 종목 코드 입력 후 AI 분석 즉시 실행 (super, 비용 발생)
 *   4) 비용 통계   — ticker_ai_tag_meta 일별/월별 합계 (super)
 */

type Tab = 'master' | 'ticker' | 'ai' | 'cost';

const CATEGORY_KO: Record<string, string> = {
  style: '스타일', size: '규모', sector: '섹터', theme: '테마',
  trait: '특성', risk: '리스크', valuechain: '밸류체인',
};

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'master', label: '태그 마스터' },
  { key: 'ticker', label: '종목 태그' },
  { key: 'ai', label: 'AI 트리거' },
  { key: 'cost', label: '비용 통계' },
];

export function TagManagementPage() {
  const [tab, setTab] = useState<Tab>('master');
  return (
    <div className="w-full">
      <div className="mb-4">
        <h1 className="text-2xl font-extrabold">종목 태그 관리</h1>
        <p className="mt-1 text-xs text-[var(--color-text-sub)]">
          85개 태그 마스터, 종목별 수동 분류, AI 분석 트리거, 비용 통계
        </p>
      </div>

      <nav className="mb-4 flex gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'bg-[var(--color-surface)] text-[var(--color-text)]'
                : 'text-[var(--color-text-sub)] hover:text-[var(--color-text)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'master' && <MasterPanel />}
      {tab === 'ticker' && <TickerPanel />}
      {tab === 'ai' && <AiTriggerPanel />}
      {tab === 'cost' && <CostStatsPanel />}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  태그 마스터
 * ════════════════════════════════════════════════════════════════ */
interface MasterTag {
  id: number;
  slug: string;
  name: string;
  category: string;
  description: string | null;
  color_class: string | null;
  conflict_group: string | null;
  is_active: boolean;
  sort_order: number;
  ticker_count: number;
}

function MasterPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tags', 'master'],
    queryFn: () => get<{ tags: MasterTag[] }>('/admin/tags'),
  });

  const tags = data?.tags ?? [];
  const filtered = useMemo(
    () => (filter === 'all' ? tags : tags.filter((t) => t.category === filter)),
    [tags, filter],
  );
  const categories = ['all', 'risk', 'size', 'theme', 'style', 'sector', 'valuechain', 'trait'];

  const toggleActiveMu = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      patch(`/admin/tags/${id}`, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'master'] });
      toast.success('태그 상태 변경됨');
    },
    onError: (e: any) => toast.error(e?.message ?? '실패'),
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={cn(
              'rounded px-2 py-0.5 text-xs',
              filter === c
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:bg-[var(--color-border)]',
            )}
          >
            {c === 'all' ? '전체' : CATEGORY_KO[c] ?? c}
          </button>
        ))}
        <span className="ml-auto text-xs text-[var(--color-text-sub)]">
          총 {filtered.length}개
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left text-xs text-[var(--color-text-sub)]">
              <tr>
                <th className="p-2">카테고리</th>
                <th className="p-2">슬럭</th>
                <th className="p-2">이름</th>
                <th className="p-2">설명</th>
                <th className="p-2 text-right">종목수</th>
                <th className="p-2 text-center">활성</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-[var(--color-border)]">
                  <td className="p-2 text-xs">{CATEGORY_KO[t.category] ?? t.category}</td>
                  <td className="p-2">
                    <TagBadge
                      slug={t.slug}
                      name={t.slug}
                      category={t.category as TagCategory}
                      size="sm"
                      asLink={false}
                    />
                  </td>
                  <td className="p-2 font-medium">{t.name}</td>
                  <td className="p-2 text-xs text-[var(--color-text-sub)]">
                    {t.description ?? '-'}
                  </td>
                  <td className="p-2 text-right tabular-nums">{t.ticker_count}</td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() =>
                        toggleActiveMu.mutate({ id: t.id, is_active: !t.is_active })
                      }
                      className={cn(
                        'rounded px-2 py-0.5 text-[10px] font-semibold',
                        t.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300',
                      )}
                      disabled={toggleActiveMu.isPending}
                    >
                      {t.is_active ? '활성' : '비활성'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  종목 태그 (수동 override)
 * ════════════════════════════════════════════════════════════════ */
interface TickerTagsResponse {
  ticker: { ticker: string; name: string; market: string } | null;
  tags: Array<{
    tag_id: number;
    slug: string;
    name: string;
    category: string;
    color_class: string | null;
    source: 'rule' | 'ai' | 'manual';
    confidence: string;
    note: string | null;
    assigned_at: string;
  }>;
  aiMeta: {
    status: string;
    last_analyzed_at: string | null;
    last_cost_usd: string | null;
    analysis_count: number;
    last_reason: string | null;
    error_message: string | null;
  } | null;
  canAiAnalyze: boolean;
  hasBusinessDesc: boolean;
}

interface AiTriggerResult {
  ticker: string;
  name: string;
  themes: Array<{ slug: string; confidence: number; reason: string }>;
  valuechain: { slug: string; confidence: number; reason: string } | null;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  inserted: number;
}

function TickerPanel() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<number | ''>('');

  const { data: tickerData, isLoading: tLoading } = useQuery({
    queryKey: ['admin', 'tags', 'ticker', submitted],
    queryFn: () => get<TickerTagsResponse>(`/admin/tags/ticker/${submitted}`),
    enabled: /^[0-9A-Z]{6}$/.test(submitted),
  });

  const { data: masterData } = useQuery({
    queryKey: ['admin', 'tags', 'master'],
    queryFn: () => get<{ tags: MasterTag[] }>('/admin/tags'),
  });
  const activeMaster = (masterData?.tags ?? []).filter((t) => t.is_active);

  const addMu = useMutation({
    mutationFn: () =>
      post(`/admin/tags/ticker/${submitted}/manual`, { tagId: selectedTagId }),
    onSuccess: () => {
      toast.success('수동 태그 추가됨');
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'ticker', submitted] });
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'master'] });
      setSelectedTagId('');
    },
    onError: (e: any) => toast.error(e?.message ?? '실패'),
  });

  const removeManualMu = useMutation({
    mutationFn: (tagId: number) =>
      del(`/admin/tags/ticker/${submitted}/manual/${tagId}`),
    onSuccess: () => {
      toast.success('수동 태그 제거됨');
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'ticker', submitted] });
    },
    onError: (e: any) => toast.error(e?.message ?? '실패'),
  });

  const forceRemoveMu = useMutation({
    mutationFn: (tagId: number) =>
      del<{ removedSource: string }>(`/admin/tags/ticker/${submitted}/${tagId}`),
    onSuccess: (data) => {
      toast.success(`태그 제거됨 (source=${data.removedSource})`);
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'ticker', submitted] });
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'master'] });
    },
    onError: (e: any) => toast.error(e?.message ?? '실패'),
  });

  const aiTriggerMu = useMutation({
    mutationFn: () => post<AiTriggerResult>(`/admin/tags/ai-trigger/${submitted}`),
    onSuccess: (data) => {
      toast.success(
        `AI 재분석 완료 — themes=${data.themes.length}개 / vc=${data.valuechain?.slug ?? '-'} / $${data.costUsd.toFixed(5)}`,
      );
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'ticker', submitted] });
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'master'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tags', 'cost-stats'] });
    },
    onError: (e: any) => toast.error(e?.message ?? 'AI 분석 실패'),
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <TickerSearchInput
          value={code}
          onSelect={(t) => {
            setCode(t);
            setSubmitted(t);
          }}
          placeholder="종목명 또는 코드 (예: 삼성전자, 005930)"
        />
      </div>

      {submitted && tLoading && <Skeleton className="h-40" />}

      {tickerData?.ticker && (
        <>
          <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-baseline gap-2">
                  <h2 className="text-lg font-bold">{tickerData.ticker.name}</h2>
                  <span className="font-mono text-xs text-[var(--color-text-sub)]">
                    {tickerData.ticker.ticker}
                  </span>
                  <span className="text-xs text-[var(--color-text-sub)]">
                    ({tickerData.ticker.market})
                  </span>
                </div>
                {tickerData.aiMeta && (
                  <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
                    AI: status={tickerData.aiMeta.status} · count={tickerData.aiMeta.analysis_count}
                    {tickerData.aiMeta.last_cost_usd != null &&
                      ` · 마지막 $${Number(tickerData.aiMeta.last_cost_usd).toFixed(5)}`}
                    {tickerData.aiMeta.error_message &&
                      ` · 오류: ${tickerData.aiMeta.error_message.slice(0, 60)}`}
                  </p>
                )}
              </div>
              {tickerData.canAiAnalyze ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (
                      confirm(
                        `${tickerData.ticker!.ticker} AI 재분석을 즉시 실행합니다. (예상 비용 약 $0.01)\n기존 AI 태그는 새 결과로 swap됩니다 (rule/manual은 보호).\n계속하시겠습니까?`,
                      )
                    ) aiTriggerMu.mutate();
                  }}
                  disabled={aiTriggerMu.isPending}
                  loading={aiTriggerMu.isPending}
                >
                  AI 재분석 ($0.01)
                </Button>
              ) : null}
            </div>

            {!tickerData.canAiAnalyze && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-700">
                ⚠️ <strong>AI 분석 불가</strong> —{' '}
                {!/^\d{6}$/.test(tickerData.ticker.ticker)
                  ? '국내 6자리 숫자 종목코드가 아닙니다 (외국기업·스팩 등은 DART API 미지원).'
                  : 'DART 사업보고서 본문이 수집되지 않았습니다 (ticker_business_desc 없음).'}
                <br />
                이 종목은 수동 태그 추가/제거로만 분류 조정 가능합니다.
              </div>
            )}
          </section>

          <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="mb-2 text-sm font-bold">현재 태그 ({tickerData.tags.length}개)</h3>
            {tickerData.tags.length === 0 ? (
              <p className="text-xs text-[var(--color-text-sub)]">태그 없음</p>
            ) : (
              <div className="space-y-1">
                {tickerData.tags.map((t) => (
                  <div
                    key={t.tag_id}
                    className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-xs"
                  >
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] font-medium',
                        t.source === 'manual'
                          ? 'bg-purple-100 text-purple-700'
                          : t.source === 'ai'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700',
                      )}
                    >
                      {t.source}
                    </span>
                    <TagBadge
                      slug={t.slug}
                      name={t.name}
                      category={t.category as TagCategory}
                      size="sm"
                      asLink={false}
                    />
                    <span className="text-[var(--color-text-sub)]">
                      conf={Number(t.confidence).toFixed(2)}
                    </span>
                    {t.note && (
                      <span className="min-w-0 flex-1 truncate text-[var(--color-text-sub)]">
                        {t.note}
                      </span>
                    )}
                    {t.source === 'manual' ? (
                      <button
                        onClick={() => {
                          if (confirm(`"${t.name}" 수동 태그 제거?`)) removeManualMu.mutate(t.tag_id);
                        }}
                        className="ml-auto rounded bg-red-50 px-2 py-0.5 text-[10px] text-red-600 hover:bg-red-100"
                        disabled={removeManualMu.isPending}
                      >
                        제거
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          const warn = t.source === 'ai'
                            ? '⚠️ AI 태그를 강제 삭제합니다. 다음 AI 재분석 시 다시 부여될 수 있습니다.'
                            : '⚠️ 규칙 태그를 강제 삭제합니다. 다음 분기 배치 시 다시 부여될 수 있습니다.';
                          if (confirm(`${warn}\n\n"${t.name}" 태그를 제거할까요?`)) {
                            forceRemoveMu.mutate(t.tag_id);
                          }
                        }}
                        className="ml-auto rounded bg-red-50 px-2 py-0.5 text-[10px] text-red-600 hover:bg-red-100"
                        disabled={forceRemoveMu.isPending}
                        title={`${t.source} 태그 강제 삭제 (super 권한)`}
                      >
                        제거
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="mb-2 text-sm font-bold">수동 태그 추가</h3>
            <div className="flex gap-2">
              <select
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(Number(e.target.value))}
                className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm"
              >
                <option value="">— 추가할 태그 선택 —</option>
                {activeMaster.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{CATEGORY_KO[t.category] ?? t.category}] {t.name} ({t.slug})
                  </option>
                ))}
              </select>
              <Button
                onClick={() => selectedTagId && addMu.mutate()}
                disabled={!selectedTagId || addMu.isPending}
                loading={addMu.isPending}
              >
                추가
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  AI 트리거
 * ════════════════════════════════════════════════════════════════ */
interface AiTriggerResponse {
  ticker: string;
  name: string;
  themes: Array<{ slug: string; confidence: number; reason: string }>;
  valuechain: { slug: string; confidence: number; reason: string } | null;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  inserted: number;
}

function AiTriggerPanel() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<AiTriggerResponse | null>(null);

  const triggerMu = useMutation({
    mutationFn: () => post<AiTriggerResponse>(`/admin/tags/ai-trigger/${code.trim()}`),
    onSuccess: (data) => {
      setResult(data);
      toast.success(`AI 분석 완료 — $${data.costUsd.toFixed(5)}`);
    },
    onError: (e: any) => toast.error(e?.message ?? 'AI 분석 실패'),
  });

  const onSubmit = () => {
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error('국내 종목코드 6자리 숫자만 가능합니다');
      return;
    }
    if (
      !confirm(
        `${code.trim()} AI 분석을 즉시 실행합니다. (예상 비용 약 $0.01)\n계속하시겠습니까?`,
      )
    ) return;
    setResult(null);
    triggerMu.mutate();
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h3 className="mb-2 text-sm font-bold">AI 분석 즉시 실행 (super)</h3>
        <p className="mb-3 text-xs text-[var(--color-text-sub)]">
          종목코드를 입력하고 실행하면 Claude Sonnet 4 호출 — 종목당 약 $0.01 비용 발생.
          ticker_business_desc에 사업의 내용이 없으면 실패합니다.
        </p>
        <div className="flex gap-2">
          <TickerSearchInput
            value={code}
            onSelect={(t) => setCode(t)}
            digitsOnly
            placeholder="종목명 또는 코드 (예: 삼성전자, 005930)"
          />
          <Button onClick={onSubmit} disabled={triggerMu.isPending} loading={triggerMu.isPending}>
            AI 분석 실행
          </Button>
        </div>
      </section>

      {result && (
        <section className="rounded-lg border border-[var(--color-primary)]/40 bg-[var(--color-surface)] p-4">
          <h3 className="mb-2 text-sm font-bold">
            결과: {result.name} ({result.ticker}) — ${result.costUsd.toFixed(5)}
          </h3>
          <p className="mb-2 text-[11px] text-[var(--color-text-sub)]">
            tokens={result.inputTokens}/{result.outputTokens} · 부여 {result.inserted}개
          </p>
          {result.themes.length > 0 && (
            <div className="mb-3">
              <h4 className="mb-1 text-xs font-semibold">테마 ({result.themes.length}개)</h4>
              <ul className="space-y-1 text-xs">
                {result.themes.map((t) => (
                  <li key={t.slug} className="flex gap-2">
                    <span className="font-mono text-orange-600">{t.slug}</span>
                    <span className="text-[var(--color-text-sub)]">
                      conf={t.confidence.toFixed(2)}
                    </span>
                    <span className="flex-1 italic">{t.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.valuechain && (
            <div>
              <h4 className="mb-1 text-xs font-semibold">밸류체인</h4>
              <div className="flex gap-2 text-xs">
                <span className="font-mono text-indigo-600">{result.valuechain.slug}</span>
                <span className="text-[var(--color-text-sub)]">
                  conf={result.valuechain.confidence.toFixed(2)}
                </span>
                <span className="flex-1 italic">{result.valuechain.reason}</span>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  비용 통계
 * ════════════════════════════════════════════════════════════════ */
interface CostStatsData {
  totals: {
    totalCalls: number;
    totalCostUsd: number;
    idleCount: number;
    failedCount: number;
  };
  daily: Array<{ day: string; calls: number; costUsd: number }>;
  monthly: Array<{ month: string; calls: number; costUsd: number }>;
}

function CostStatsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tags', 'cost-stats'],
    queryFn: () => get<CostStatsData>('/admin/tags/cost-stats'),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="누적 호출" value={data.totals.totalCalls.toLocaleString()} />
        <StatCard label="누적 비용" value={`$${data.totals.totalCostUsd.toFixed(4)}`} />
        <StatCard label="idle" value={data.totals.idleCount.toString()} />
        <StatCard label="failed" value={data.totals.failedCount.toString()} variant={data.totals.failedCount > 0 ? 'danger' : undefined} />
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DataTable title="일별 (최근 30일)" rows={data.daily.map((r) => [r.day, r.calls, r.costUsd])} />
        <DataTable title="월별 (최근 12개월)" rows={data.monthly.map((r) => [r.month, r.calls, r.costUsd])} />
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  variant,
}: { label: string; value: string; variant?: 'danger' }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3',
        variant === 'danger' && 'border-red-300 bg-red-50',
      )}
    >
      <div className="text-xs text-[var(--color-text-sub)]">{label}</div>
      <div className={cn('mt-1 text-2xl font-bold tabular-nums', variant === 'danger' && 'text-red-600')}>
        {value}
      </div>
    </div>
  );
}

function DataTable({ title, rows }: { title: string; rows: Array<[string, number, number]> }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <h3 className="mb-2 text-sm font-bold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-[var(--color-text-sub)]">데이터 없음</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[var(--color-text-sub)]">
            <tr>
              <th className="p-1 text-left">기간</th>
              <th className="p-1 text-right">호출</th>
              <th className="p-1 text-right">비용</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[0]} className="border-t border-[var(--color-border)]">
                <td className="p-1 tabular-nums">{r[0]}</td>
                <td className="p-1 text-right tabular-nums">{r[1]}</td>
                <td className="p-1 text-right tabular-nums">${r[2].toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  종목 검색 입력 — 종목명/코드 둘 다 받기
 *  /search/autocomplete (kind='ticker' 만 필터)
 * ════════════════════════════════════════════════════════════════ */
interface AutocompleteSuggestion {
  kind: 'ticker' | 'user';
  value: string;
  label: string;
  sub: string | null;
}

interface TickerSearchInputProps {
  /** 선택된 종목 (코드) — 부모가 관리 */
  value: string;
  /** 종목 선택 시 콜백 (코드만 전달) */
  onSelect: (ticker: string) => void;
  /** 6자리 숫자만 허용할지 여부 (AI 트리거는 국내주식만) */
  digitsOnly?: boolean;
  placeholder?: string;
}

function TickerSearchInput({
  value,
  onSelect,
  digitsOnly = false,
  placeholder = '종목명 또는 코드 (예: 삼성전자, 005930)',
}: TickerSearchInputProps) {
  const [input, setInput] = useState(value);
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLFormElement>(null);

  /* 부모 value 동기화 (예: 외부에서 클리어) */
  useEffect(() => setInput(value), [value]);

  /* 300ms debounce */
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data } = useQuery({
    queryKey: ['admin', 'ticker-autocomplete', debounced, digitsOnly],
    queryFn: () =>
      get<{ suggestions: AutocompleteSuggestion[] }>(
        `/search/autocomplete?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length >= 1,
    staleTime: 60_000,
  });

  /* ticker 만 필터, digitsOnly 면 6자리 숫자만 */
  const suggestions = useMemo(() => {
    const list = (data?.suggestions ?? []).filter((s) => s.kind === 'ticker');
    return digitsOnly ? list.filter((s) => /^\d{6}$/.test(s.value)) : list;
  }, [data, digitsOnly]);

  /* 외부 클릭 시 닫기 */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePick = (s: AutocompleteSuggestion) => {
    setInput(`${s.label} (${s.value})`);
    setOpen(false);
    onSelect(s.value);
  };

  /* Enter 키: 6자리 코드 직접 입력했으면 그대로 사용, 아니면 첫 후보 사용 */
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    /* 사용자가 코드 직접 입력한 케이스 (6자리 숫자/영숫자) */
    if (digitsOnly ? /^\d{6}$/.test(trimmed) : /^[0-9A-Z]{6}$/.test(trimmed.toUpperCase())) {
      onSelect(trimmed.toUpperCase());
      setOpen(false);
      return;
    }
    /* 종목명 입력 → 첫 후보 사용 */
    if (suggestions.length > 0) {
      handlePick(suggestions[0]);
    }
  };

  return (
    <form ref={containerRef} onSubmit={onSubmit} className="relative flex-1">
      <input
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm focus:border-[var(--color-primary)] focus:outline-none"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-md">
          {suggestions.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => handlePick(s)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-bg)]"
            >
              <span className="font-medium">{s.label}</span>
              <span className="font-mono text-[10px] text-[var(--color-text-sub)]">
                {s.value}
              </span>
              {s.sub && (
                <span className="ml-auto text-[10px] text-[var(--color-text-sub)]">
                  {s.sub}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
